import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plug, Plus, Settings2, Trash2 } from "lucide-react";
import { IconPicker } from "@/components/icon-picker";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { AVATAR_ICONS } from "@/lib/avatars";
import { mcpSubtitle, parsePairs, parseToolNames, type McpChoice } from "@/lib/mcp-tools";
import { qk } from "@/lib/query/keys";
import type { AgentMcpServer, AgentWithContext, McpServer, McpServerKind } from "@/lib/types";
import { cn } from "@/lib/utils";

const selectServers = (data: { servers: McpServer[] }) => data.servers;

/** An MCP server's icon, from the same set an agent's avatar draws from. */
export function McpIcon({ icon, className }: { icon: string; className?: string }) {
  const Icon = AVATAR_ICONS[icon] ?? Plug;
  return <Icon className={cn("size-4 shrink-0", className)} aria-hidden />;
}

/**
 * The agent's MCP tools behind one small icon with a count: a dropdown shaped like
 * the sidebar's Boards one — a label, a row per attached server (icon, name, a muted
 * line saying how much of it), then the actions. Admins get Manage and Attach;
 * anyone else sees the names and icons and nothing to press. Nothing in it removes
 * anything: detaching is in the agent's dialog, where it is a tick.
 */
export function McpToolsButton({
  agent,
  canManage,
  onManage,
  className,
}: {
  agent: Pick<AgentWithContext, "name" | "mcpServers">;
  canManage: boolean;
  /** Opens the agent's dialog at its MCP tools. */
  onManage: () => void;
  className?: string;
}) {
  const servers = agent.mcpServers ?? [];
  // A viewer has nothing to do with an agent that carries none.
  if (servers.length === 0 && !canManage) return null;
  return (
    <DropdownMenu>
      <Hint label={`MCP tools · ${servers.length}`}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" className={cn("relative", className)} aria-label={`MCP tools of ${agent.name}: ${servers.length}`}>
            <Plug className={cn(servers.length === 0 && "opacity-60")} />
            {servers.length > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 grid min-w-3.5 place-items-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-3.5 text-primary-foreground tabular-nums">
                {servers.length}
              </span>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>MCP tools · {servers.length}</DropdownMenuLabel>
        <div className="max-h-[60dvh] overflow-y-auto scrollbar-slim">
          {servers.length === 0 ? <p className="px-2 py-1.5 text-xs text-muted-foreground">No MCP tools attached</p> : null}
          {servers.map((server) => (
            <McpRow key={server.id} server={server} onSelect={canManage ? onManage : undefined} />
          ))}
        </div>
        {canManage ? (
          <>
            <DropdownMenuSeparator />
            {servers.length > 0 ? (
              <DropdownMenuItem onSelect={onManage}>
                <Settings2 />
                Manage…
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={onManage}>
              <Plus />
              Attach a tool
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function McpRow({ server, onSelect }: { server: AgentMcpServer; onSelect?: () => void }) {
  const body = (
    <>
      <McpIcon icon={server.icon} className="text-primary" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium">{server.name}</span>
        <span className="block truncate text-[11px] text-muted-foreground">{mcpSubtitle(server)}</span>
      </span>
    </>
  );
  return onSelect ? (
    <DropdownMenuItem onSelect={onSelect} className="items-center gap-2.5">
      {body}
    </DropdownMenuItem>
  ) : (
    <div className="flex items-center gap-2.5 px-2 py-1.5">{body}</div>
  );
}

/** A list row's quiet marker: the plug and how many, with the names on hover. Not a button — the row opens the chat. */
export function McpCountChip({ agent, className }: { agent: Pick<AgentWithContext, "mcpServers">; className?: string }) {
  const servers = agent.mcpServers ?? [];
  if (servers.length === 0) return null;
  return (
    <span
      title={`MCP tools: ${servers.map((server) => server.name).join(", ")}`}
      className={cn("inline-flex shrink-0 items-center gap-0.5 text-[10.5px] tabular-nums text-muted-foreground", className)}
    >
      <Plug className="size-3" aria-hidden />
      {servers.length}
    </span>
  );
}

/* --------------------------------------------------------- the dialog's section */

const KINDS: { kind: McpServerKind; label: string; hint: string }[] = [
  { kind: "http", label: "HTTP", hint: "A remote server at a URL" },
  { kind: "stdio", label: "Command", hint: "A program run on this machine" },
  { kind: "connector", label: "Connector", hint: "A claude.ai connector, such as Microsoft 365" },
];

/**
 * "MCP tools" in the agent dialog: every registered server with its icon, a tick to
 * attach it, "all tools" or a chosen few, and — for admins — a form to register one.
 * Nothing here asks for a tool name unless the server lists none.
 */
export function McpToolsField({
  value,
  onChange,
  onPendingChange,
  nudge = false,
}: {
  value: McpChoice[];
  onChange: (next: McpChoice[]) => void;
  /** True while the register form holds a name that was never registered, so the dialog's Save can refuse to drop it. */
  onPendingChange?: (pending: boolean) => void;
  /** Save was pressed with the register form unfinished: say so beside its Register button. */
  nudge?: boolean;
}) {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.mcpServers(), queryFn: api.listMcpServers, select: selectServers });
  const servers = query.data ?? [];
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attached = (name: string) => value.find((choice) => choice.server === name);

  const toggle = (server: McpServer) =>
    onChange(attached(server.name) ? value.filter((choice) => choice.server !== server.name) : [...value, { server: server.name, tools: [] }]);
  const setTools = (name: string, tools: string[]) => onChange(value.map((choice) => (choice.server === name ? { ...choice, tools } : choice)));

  const remove = async (server: McpServer) => {
    const used = server.agentCount > 0 ? ` It is attached to ${server.agentCount} agent${server.agentCount === 1 ? "" : "s"}; they lose it.` : "";
    if (!window.confirm(`Delete the ${server.name} server from the registry?${used}`)) return;
    try {
      await api.deleteMcpServer(server.id);
      onChange(value.filter((choice) => choice.server !== server.name));
      void client.invalidateQueries({ queryKey: qk.mcpServers() });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete the server");
    }
  };

  return (
    <section id="agent-mcp" className="space-y-2 rounded-md border border-border/70 p-3" aria-label="MCP tools">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5">
          <Plug className="size-3.5 text-primary" /> MCP tools
        </Label>
        {!registering ? (
          <Button type="button" variant="ghost" size="xs" onClick={() => setRegistering(true)}>
            <Plus /> Register a server
          </Button>
        ) : null}
      </div>
      {servers.length === 0 && !registering ? (
        <p className="text-[11.5px] text-muted-foreground">
          No servers registered yet. Register one, then tick it here to give this agent its tools — no tool names to type.
        </p>
      ) : null}
      <ul className="space-y-1">
        {servers.map((server) => {
          const choice = attached(server.name);
          return (
            <li key={server.id} className="rounded-md border border-transparent px-1.5 py-1 hover:bg-muted/40">
              <div className="flex items-center gap-2.5">
                <input
                  id={`mcp-${server.id}`}
                  type="checkbox"
                  className="size-4 accent-[var(--primary)]"
                  checked={Boolean(choice)}
                  onChange={() => toggle(server)}
                />
                <label htmlFor={`mcp-${server.id}`} className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                  <McpIcon icon={server.icon} className="text-primary" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">{server.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {server.kind}
                      {server.description ? ` · ${server.description}` : server.tools.length ? ` · ${server.tools.length} tools` : ""}
                    </span>
                  </span>
                </label>
                <Button type="button" variant="ghost" size="icon-sm" aria-label={`Delete ${server.name}`} onClick={() => void remove(server)}>
                  <Trash2 />
                </Button>
              </div>
              {choice ? <ToolChoice server={server} tools={choice.tools} onChange={(tools) => setTools(server.name, tools)} /> : null}
            </li>
          );
        })}
      </ul>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {registering ? (
        <RegisterForm
          onDirty={onPendingChange}
          nudge={nudge}
          onCancel={() => {
            setRegistering(false);
            onPendingChange?.(false);
          }}
          onRegistered={(server) => {
            setRegistering(false);
            onPendingChange?.(false);
            void client.invalidateQueries({ queryKey: qk.mcpServers() });
            onChange([...value.filter((choice) => choice.server !== server.name), { server: server.name, tools: [] }]);
          }}
        />
      ) : null}
    </section>
  );
}

/** Under a ticked server: every tool of it, or only the ones ticked / typed. */
function ToolChoice({ server, tools, onChange }: { server: McpServer; tools: string[]; onChange: (tools: string[]) => void }) {
  const [only, setOnly] = useState(tools.length > 0);
  const [typed, setTyped] = useState(tools.join(", "));
  return (
    <div className="mt-1.5 space-y-1.5 pl-[26px] text-[12px]">
      <div className="flex gap-3">
        {[false, true].map((mode) => (
          <label key={String(mode)} className="flex cursor-pointer items-center gap-1.5">
            <input
              type="radio"
              className="accent-[var(--primary)]"
              name={`mcp-mode-${server.id}`}
              checked={only === mode}
              onChange={() => {
                setOnly(mode);
                if (!mode) onChange([]);
              }}
            />
            {mode ? "Only these" : "All tools"}
          </label>
        ))}
      </div>
      {only && server.tools.length > 0 ? (
        <div className="grid max-h-36 gap-0.5 overflow-y-auto scrollbar-slim sm:grid-cols-2">
          {server.tools.map((tool) => (
            <label key={tool} className="flex cursor-pointer items-center gap-1.5 truncate font-mono text-[11px]">
              <input
                type="checkbox"
                className="accent-[var(--primary)]"
                checked={tools.includes(tool)}
                onChange={() => onChange(tools.includes(tool) ? tools.filter((entry) => entry !== tool) : [...tools, tool])}
              />
              <span className="truncate">{tool}</span>
            </label>
          ))}
        </div>
      ) : null}
      {only && server.tools.length === 0 ? (
        <Input
          aria-label={`Tools of ${server.name}`}
          className="h-8 font-mono text-[12px]"
          placeholder="tool names, separated by commas"
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            onChange(parseToolNames(event.target.value));
          }}
        />
      ) : null}
    </div>
  );
}

function RegisterForm({
  onCancel,
  onRegistered,
  onDirty,
  nudge = false,
}: {
  onCancel: () => void;
  onRegistered: (server: McpServer) => void;
  onDirty?: (dirty: boolean) => void;
  nudge?: boolean;
}) {
  const [kind, setKind] = useState<McpServerKind>("http");
  const [name, setName] = useState("");
  const [icon, setIcon] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [command, setCommand] = useState("");
  const [args, setArgs] = useState("");
  const [secrets, setSecrets] = useState("");
  const [tools, setTools] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = name.trim().length > 0;
  useEffect(() => onDirty?.(dirty), [dirty, onDirty]);
  useEffect(() => () => onDirty?.(false), [onDirty]);

  const submit = async () => {
    setError(null);
    const parsed = kind === "connector" ? { pairs: {} } : parsePairs(secrets, kind === "stdio" ? "=" : ":");
    if ("badLine" in parsed) {
      setError(`${kind === "stdio" ? "Environment" : "Headers"}: line ${parsed.badLine} is not ${kind === "stdio" ? "NAME=value" : "Name: value"}.`);
      return;
    }
    setSaving(true);
    try {
      const server = await api.createMcpServer({
        kind,
        name: name.trim(),
        icon,
        description: description.trim() || null,
        tools: parseToolNames(tools),
        ...(kind === "http" ? { url: url.trim(), headers: parsed.pairs } : {}),
        ...(kind === "stdio" ? { command: command.trim(), args: args.trim() ? args.trim().split(/\s+/) : [], env: parsed.pairs } : {}),
      });
      onRegistered(server);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not register the server");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2.5 rounded-md border border-border/70 bg-muted/20 p-2.5">
      <div className="flex gap-1.5" role="group" aria-label="Kind of server">
        {KINDS.map((entry) => (
          <Button key={entry.kind} type="button" size="xs" variant={kind === entry.kind ? "default" : "outline"} title={entry.hint} onClick={() => setKind(entry.kind)}>
            {entry.label}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-[auto_1fr] items-end gap-2">
        <button
          type="button"
          aria-label="Choose the icon"
          aria-expanded={picking}
          onClick={() => setPicking((open) => !open)}
          className="grid size-9 place-items-center rounded-md border border-input bg-background/60 text-primary"
        >
          <McpIcon icon={icon ?? "server"} />
        </button>
        <div className="space-y-1">
          <Label htmlFor="mcp-name">Name</Label>
          <Input
            id="mcp-name"
            value={name}
            placeholder={kind === "connector" ? "claude_ai_Microsoft_365" : "docs"}
            onChange={(event) => setName(event.target.value.replace(/\s+/g, "_"))}
          />
        </div>
      </div>
      {picking ? <IconPicker icon={icon} color={null} onChange={(next) => setIcon(next.icon)} /> : null}
      {kind === "http" ? (
        <div className="space-y-1">
          <Label htmlFor="mcp-url">URL</Label>
          <Input id="mcp-url" value={url} placeholder="https://example.com/mcp" onChange={(event) => setUrl(event.target.value)} />
        </div>
      ) : null}
      {kind === "stdio" ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="mcp-command">Command</Label>
            <Input id="mcp-command" value={command} placeholder="npx" onChange={(event) => setCommand(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mcp-args">Arguments</Label>
            <Input id="mcp-args" value={args} placeholder="-y some-mcp-server" onChange={(event) => setArgs(event.target.value)} />
          </div>
        </div>
      ) : null}
      {kind !== "connector" ? (
        <div className="space-y-1">
          <Label htmlFor="mcp-secrets">{kind === "stdio" ? "Environment (one NAME=value per line)" : "Headers (one Name: value per line)"}</Label>
          <Textarea
            id="mcp-secrets"
            rows={2}
            className="font-mono text-[12px]"
            autoComplete="off"
            spellCheck={false}
            value={secrets}
            placeholder={kind === "stdio" ? "API_TOKEN=…" : "Authorization: Bearer …"}
            onChange={(event) => setSecrets(event.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">Entered once and never shown again — only the names are kept visible.</p>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          A connector comes from your Claude account, so there is nothing to connect here — its name is the one the CLI gives it, starting claude_ai_.
        </p>
      )}
      <div className="space-y-1">
        <Label htmlFor="mcp-tools-list">Tools it has (optional)</Label>
        <Input
          id="mcp-tools-list"
          value={tools}
          placeholder="listed, they can be ticked one by one"
          onChange={(event) => setTools(event.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="mcp-desc">Description (optional)</Label>
        <Input id="mcp-desc" value={description} onChange={(event) => setDescription(event.target.value)} />
      </div>
      {kind === "stdio" ? (
        <p className="text-[11px] leading-snug text-muted-foreground">
          A command server runs on this machine as the worker's user, with whatever it is given — register only programs you trust.
        </p>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {nudge && !error ? <p className="text-xs text-destructive">Not registered yet — press Register (or Cancel) before saving the agent.</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" loading={saving} disabled={!name.trim()} onClick={() => void submit()}>
          Register
        </Button>
      </div>
    </div>
  );
}
